<template>
  <modal
    ref="modalComponent"
    size="lg"
    :show="show"
    submit-button-text="Give this award"
    :is-submitting="isSubmitting"
    :scrollable="false"
    modal-class="award-modal"
    @hidden="$emit('input', false)"
    @submit="onSubmit"
  >
    <div slot="modal-title" class="text-extra-large">
      Give an award
      <div class="award-icon-circle flex-center"><i class="award-icon"></i></div>
    </div>
    <div class="form-field">
      <label class="text-medium-1 ml-2">Who is this award for (person or group of people)</label>
      <users-multi-select
        id="awardee"
        class="multi-select"
        :error="errors.recipientIds"
        :org-id="context.orgId"
        :team-id="teamId"
        :selected="selectedUsers"
        @change="onUserSelect"
      />
    </div>
    <div class="form-field">
      <label class="text-medium-1 ml-2">What is this award towards</label>
      <div class="context-field d-flex align-items-center">
        <div class="d-inline-flex context-item" v-if="isKrAward || isGoalAward">
          <i
            :class="{
              'goal-icon': isGoalAward,
              'kr-icon': isKrAward,
              'entity-icon-container': true
            }"
          ></i>
          <div class="ml-2" v-no-capture>{{ context.contextTitle }}</div>
        </div>
        <div class="d-inline-flex context-item" v-else-if="isInitiativeAward">
          <b-form-checkbox :checked="false" :disabled="true"></b-form-checkbox>
          <div class="initiative-title" v-no-capture>{{ context.contextTitle }}</div>
        </div>
      </div>
    </div>
    <div class="form-field mb-3" id="pick-badge">
      <label class="ml-2">Choose a badge and write a note</label>
      <div class="d-flex content-field">
        <div class="badge-wrapper">
          <award-badge-picker @change="onBadgeSelect" :selected="selectedBadge" />
        </div>
        <div class="flex-grow-1 editor-wrapper" v-if="usersInScope.length > 0">
          <ck-editor
            @change="onNoteChange"
            ref="awardNote"
            :config="ckeditorConfig"
            id="award-note"
            @save="onSubmit"
          />
          <div v-if="errors.note" class="note-error field-error-message">{{ errors.note }}</div>
        </div>
      </div>
    </div>
    <div class="form-field mb-2">
      <div class="d-flex ml-2">
        <label class="mr-2" id="share-award">Share via email:</label>
        <b-form-radio-group id="radio-group-2" v-model="emailToBeSentTo" name="radio-sub-component">
          <b-form-radio value="organization">Everyone in your Org</b-form-radio>
          <b-form-radio v-if="team && !team.isOrgLike" value="team"
            >Only <span class="bold" v-no-capture>{{ team.name }}</span> team
          </b-form-radio>
          <b-form-radio value="recipients">Award recipients only</b-form-radio>
        </b-form-radio-group>
      </div>
    </div>
    <tour :steps="AWARD_TOUR_CONFIG" :meta="TOUR.GIVE_AWARD"></tour>
  </modal>
</template>

<script>
import { getCKEditorConfig } from '@/ckeditorConfig'
import AwardBadgePicker from '@/components/award/AwardBadgePicker'
import CkEditor from '@/components/CkEditor'
import Modal from '@/components/Modal'
import Tour from '@/components/tour/Tour'
import { AWARD_TOUR_CONFIG } from '@/components/tour/tour-config/awardTour'
import { TOUR } from '@/components/tour/tour-config/tours'
import UsersMultiSelect from '@/components/UsersMultiSelect'
import { AWARD_TYPE } from '@/constants/awards'
import { REWARD_DEFAULT_BADGE } from '@/constants/badges'
import { ERROR_PATH } from '@/constants/messages'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import showAPIError from '@/utils/showAPIError'
import { BFormCheckbox, BFormRadio, BFormRadioGroup } from 'bootstrap-vue'
import * as R from 'ramda'

/**
 This component lets user give award. It opens the component in a modal.

 @emits
 - awarded
 */
export default {
  name: 'GiveAward',
  props: {
    show: Boolean,
    context: Object,
    teamId: Number,
    orgId: [Number, String]
  },
  mixins: [EventBusMixin],
  model: {
    prop: 'show'
  },
  data() {
    return {
      selectedUsers: this.context.recipientIds,
      selectedBadge: REWARD_DEFAULT_BADGE,
      emailToBeSentTo: 'organization',
      AWARD_TYPE,
      errors: {},
      AWARD_TOUR_CONFIG,
      TOUR,
      isSubmitting: false
    }
  },
  components: {
    Modal,
    UsersMultiSelect,
    CkEditor,
    AwardBadgePicker,
    BFormCheckbox,
    BFormRadioGroup,
    BFormRadio,
    Tour
  },
  computed: {
    team() {
      return this.teamId && this.$store.getters.teamById(this.teamId)
    },
    usersInScope() {
      return this.$store.getters.orgMemberships(this.context.orgId).map(x => x.user)
    },
    ckeditorConfig() {
      return getCKEditorConfig({
        height: 91,
        maxHeight: 91,
        users: this.usersInScope,
        orgId: this.context.orgId,
        startupFocus: true,
        config: {
          disableMentions: true
        }
      })
    },
    isKrAward() {
      return [AWARD_TYPE.CHECKIN, AWARD_TYPE.KEY_RESULT].some(R.equals(this.context.type))
    },
    isInitiativeAward() {
      return [
        AWARD_TYPE.INITIATIVE,
        AWARD_TYPE.INITIATIVE_STATUS,
        AWARD_TYPE.INITIATIVE_COMMENT
      ].some(R.equals(this.context.type))
    },
    isGoalAward() {
      return [AWARD_TYPE.GOAL, AWARD_TYPE.GOAL_COMMENT].includes(this.context.type)
    }
  },
  methods: {
    onBadgeSelect(badge) {
      this.selectedBadge = badge
    },
    onUserSelect(users) {
      this.selectedUsers = users
      if (users.length > 0) {
        this.removeError('recipientIds')
      }
    },
    removeError(field) {
      this.errors = R.dissoc(field, this.errors)
    },
    getAwardUrl() {
      const { orgId, goalId, initiativeId } = this.context
      if (this.isGoalAward || this.isKrAward) {
        return urlManager.goalDetail({ orgId, goalId })
      } else if (this.isInitiativeAward) {
        return urlManager.initiativeDetail({ orgId, initiativeId })
      }
    },
    async onSubmit() {
      const note = this.$refs.awardNote.getData()
      const { contextId, contextType, type } = this.context
      this.isSubmitting = true
      try {
        await service.client.post(this.getAwardUrl(), {
          action: 'award',
          shareWith: this.emailToBeSentTo,
          award: {
            caption: this.selectedBadge.caption,
            contextId,
            contextType,
            icon: this.selectedBadge.icon,
            note: note,
            recipient_ids: this.selectedUsers.filter(Boolean),
            type
          }
        })
        EventBus.$emit('ADD_TOAST', { message: 'Award has been given successfully' })
        this.$refs.modalComponent.hide()
        this.$emit('awarded')
      } catch (e) {
        this.errors = R.compose(
          R.mapObjIndexed(R.nth(0)),
          R.pathOr({}, ERROR_PATH)
        )(e)
        if (!this.errors.note && !this.errors.recipientIds) {
          showAPIError(e)
        }
      }
      this.isSubmitting = false
    },
    onNoteChange() {
      this.removeError('note')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/_errors.scss';

.award-icon-circle {
  position: absolute;
  @include dimension(100px, 100px);
  background: $documentColor;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
}

.award-icon {
  height: 67px;
  width: 62px;
  @include dimension(57px, 62px);
}

.badge-wrapper {
  width: 140px;
  border: 1px solid $tertiary;
  border-radius: 10px 0 0 10px;
  overflow: hidden;
  flex-shrink: 0;
}

.editor-wrapper {
  overflow: hidden;
  border: 1px solid $tertiary;
  border-left: none;
  border-radius: 0 10px 10px 0;
  padding-left: 12px;
  position: relative;
}

.note-error {
  position: absolute;
  left: 16px;
  bottom: 0px;
}

.context-field {
  min-height: 32px;
  padding: 0 8px;
}

.form-field {
  margin-bottom: 15px;
}

.context-item {
  border-radius: 20px;
  background-color: $quaternary;
  padding: 3px 12px;
}

.entity-icon-container {
  flex-shrink: 0;

  &.goal-icon {
    margin-top: 3px;
  }

  &.kr-icon {
    margin-top: 6px;
  }
}

@include media-breakpoint-down(md) {
  .award-icon-circle {
    display: none;
  }
}
</style>

<style lang="scss">
@import '~@/styles/_styleConfigurations.scss';

.award-modal {
  margin-top: 55px;
  max-height: 87%;
}

.content-field {
  min-height: 142px;
}

.initiative-title {
  margin-bottom: 1px;
}
</style>
